import React, { useState } from "react";

import { useGetCurrentUser } from "api/api-hooks/user-hooks";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { MdmObject, MdmObjectNote, useCreateNoteMutation, useDeleteNoteMutation, useGetAllNotesQuery } from "gql/generated/types";
import { Button } from "primereact/button";
import { Card } from 'primereact/card';
import { Editor } from "primereact/editor";

type ObjectNotesProps = {
  object: MdmObject
}

const ObjectNotes: React.FC<ObjectNotesProps> = ({ object }) => {
  const [ note, setNote ] = useState<string | null>(null)
  const [mutationFunction] = useCreateNoteMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Note created successfully",
        life: 3000,
      });
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetObject", "GetAllNotes"],
  })

  const {data} = useGetAllNotesQuery({
    variables: {
      id: object.id!
    },
    skip: !object.id
  })

  const handleNoteSend = () => {
    if (!note) return;
    mutationFunction({
      variables: {
        objectId: object.id!,
        note
      }
    })
    setNote(null)
  }

  const mapNotes = data?.allNotesByObjectId?.map(n => <ObjectNote key={n.id} note={n} />);

  return (
    <div className="mx-auto" style={{ maxWidth: "800px"}}>
      {data && data.allNotesByObjectId.length > 0 && (
        <Card style={{ maxHeight: "300px", overflowY: "auto" }}>{mapNotes}</Card>
      )}
      <Editor
        value={note ?? ""}
        onTextChange={e => setNote(e.htmlValue)}
        className="mt-3"
        style={{ minHeight: "100px" }}
      />
      <Button type="button" label="Send" onClick={handleNoteSend} className="w-auto mt-3" />
    </div>
  );
}

export default ObjectNotes;

const ObjectNote: React.FC<{note: MdmObjectNote}> = ({ note}) => {
  const {data: user} = useGetCurrentUser();
  const [mutationFunction] = useDeleteNoteMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Note deleted successfully",
        life: 3000,
      });
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetObject", "GetAllNotes"],
  })

  const confirmOnDelete = () => {
    mutationFunction({
      variables: {
        id: note.id
      }
    })
  }

  return (
    <div id="note" className="mb-3 border-1 border-300 border-round px-3 pb-2" >
      <div className="flex justify-content-end align-items-center m-0">
        <span className="text-500 mr-auto">{user?.displayName}</span>
        <span className="text-500">{new Date(note.createdAt).toLocaleString()}</span>
        <Button
          type="button"
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger p-0"
          onClick={confirmOnDelete}
        />
      </div>
      <div dangerouslySetInnerHTML={{ __html: note.note ?? "" }} style={{ wordBreak: "break-word" }} />
    </div>
  );
}